
export const buildValidationRules = (validations, is_required, reg_key, label = "") => {
  const rules = {};

  for (const validation of validations) {
    switch (validation?.key) {
      case "required":
        if (is_required === 0) {
          break;
        } else {
          rules.required =
            validation.message
              ?.replaceAll(/<[^>]*>/g, "")
              ?.replaceAll("*", "")
              ?.trim() ||
            `${label} is required`;
          break;
        }
      case "min":
      case "minlength":
        rules.minLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "max":
      case "maxlength":
        rules.maxLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "pattern":
        rules.pattern = {
          value: new RegExp(validation?.regex?.slice(1, -1)),
          message: validation.message,
        };
        break;
      case "minCount":
        rules.validate = {
          ...rules.validate,
          minCount: (value) => {
            if (Array.isArray(value) && value.length < validation.length) {
              return validation.message;
            }
            return true;
          },
        };
        break;

      case "maxCount":
        rules.validate = {
          ...rules.validate,
          maxCount: (value) => {
            if (Array.isArray(value) && value.length > validation.length) {
              return validation.message;
            }
            return true;
          },
        };
        break;

      case "size":
        rules.validate = {
          ...rules.validate,
          fileSize: (files) =>
            files?.length > 0
              ? files[0]?.size < validation?.length * 1024 ||
              validation?.message ||
              "File size exceeds the allowed limit"
              : true,
        };
        break;
      case "mimes": {
        const allowedMimeTypes =
          reg_key === "resume" || reg_key === "document"
            ? validation?.types?.map((ext) => `application/${ext}`)
            : validation?.types?.map((ext) =>
              ext === "jpg" ? "image/jpeg" : `image/${ext}`
            );
        rules.validate = {
          ...rules.validate,
          fileType: (files) => {
            if (files?.length > 0) {
              return (
                allowedMimeTypes.includes(files[0].type) ||
                validation.message ||
                "Invalid file type"
              );
            }
            return true;
          },
        };
        break;
      }
      default:
        break;
    }
  }
  return rules;
};

export const normalizeStudentSignup2Payload = ({
  data,
  roleId,
  dob,
  email_verify_string,
  selectedResume,
}) => {
  let payload = { ...data };

  payload.role_id = roleId;
  payload.step_number = "step_3";
  payload.type = "register";
  payload.role_type = roleId;
  payload.email_verify_string = email_verify_string;

  if (payload.subarea) {
    payload.subareas = payload.subarea;
  }

  if (payload.mainArea) {
    payload.domains = payload.mainArea;
  }

  payload = normalizeBooleanFields(payload);
  payload = normalizeDateFields(payload, dob);
  payload = cleanupPayloadFields(payload);

  return handleResumeInPayload(payload, selectedResume);
};

const normalizeBooleanFields = (payload) => {
  const booleanKeys = [
    "mn_open_to_talk",
    "is_privacy",
    "is_terms",
    "my_consent",
    "mn_loking_for_coach",
    "mf_mental_health",
    "mf_certified_coach",
    "lgbtq_discussion",
    "is_disclaimer",
    "training_video",
  ];

  const newPayload = { ...payload };
  for (const key of booleanKeys) {
    if (key in newPayload) {
      newPayload[key] = newPayload[key] ? 1 : 0;
    }
  }
  return newPayload;
};

const normalizeDateFields = (payload, dob) => {
  const newPayload = { ...payload };
  if ("dob" in newPayload) {
    newPayload.dob = newPayload.dob ? dob : "";
  }

  if (newPayload.availability) {
    newPayload.availability = newPayload.availability ? 2 : 1;
  } else {
    newPayload.availability = 1;
    newPayload.from_date = null;
    newPayload.to_date = null;
  }

  // Handle board_roles repeater field - convert array of objects to JSON string
  if (newPayload.board_roles && Array.isArray(newPayload.board_roles)) {
    newPayload.board_roles = JSON.stringify(newPayload.board_roles);
  }

  return newPayload;
};

const cleanupPayloadFields = (payload) => {
  const newPayload = { ...payload };

  if (
    newPayload.mainArea === "undefined" ||
    newPayload.mainArea === "null" ||
    !newPayload.mainArea
  ) {
    delete newPayload.mainArea;
  }

  if (
    !newPayload.domains ||
    newPayload.domains === "undefined" ||
    newPayload.domains === "null" ||
    newPayload.domains === null
  ) {
    delete newPayload.domains;
  }

  if (
    newPayload.language === "undefined" ||
    newPayload.language === "null" ||
    !newPayload.language
  ) {
    delete newPayload.language;
  }

  return newPayload;
};

const handleResumeInPayload = (payload, selectedResume) => {
  const newPayload = { ...payload };
  if (
    selectedResume &&
    selectedResume instanceof File &&
    selectedResume.name &&
    selectedResume.size > 0 &&
    selectedResume.type === "application/pdf"
  ) {
    newPayload.resume = selectedResume;
  } else if ("resume" in newPayload) {
    const { resume, ...rest } = newPayload;
    return rest;
  }
  return newPayload;
};

const handleImageFile = (formData, data, selectedFile) => {
  if (data.image instanceof File) {
    formData.append("image", data.image);
  } else if (selectedFile instanceof File) {
    formData.append("image", selectedFile);
  }

  if (
    "image" in data &&
    (!selectedFile || !(selectedFile instanceof File))
  ) {
    delete data.image;
  }
};

const buildMergedKeys = (allowedKeys) => {
  let mergedKeys = [
    ...allowedKeys,
    "step_number",
    "type",
    "role_type",
    "email_verify_string",
  ];

  if (allowedKeys.includes("domains")) {
    mergedKeys.push("subareas", "subarea", "mainArea");
  }

  if (allowedKeys.includes("availability")) {
    mergedKeys.push("from_date", "to_date");
  }

  const hasBoardRoleFields =
    allowedKeys.includes("rep_company_name") &&
    allowedKeys.includes("rep_board_title") &&
    allowedKeys.includes("rep_start_date") &&
    allowedKeys.includes("rep_end_date");

  if (hasBoardRoleFields) {
    mergedKeys.push("board_roles");
  }

  return mergedKeys;
};

const isValidValue = (value) => {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    value !== "undefined"
  );
};

const appendFormData = (formData, keys, data) => {
  for (const key of keys) {
    const value = data[key];

    if (!isValidValue(value)) continue;

    if (key === "board_roles" && Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  }
};

export const buildStudentSignup2FormData = ({
  data,
  registerStepFields,
  selectedFile,
}) => {
  const formData = new FormData();

  handleImageFile(formData, data, selectedFile);

  const registerStep = registerStepFields[0]?.reg_feilds || [];
  const allowedKeys = registerStep.map((field) => field.reg_key);

  const mergedKeys = buildMergedKeys(allowedKeys);

  appendFormData(formData, mergedKeys, data);

  return formData;
};

export const applyVerificationDataImmediately = ({
  data,
  setValue,
  setRoleId,
  handleChange,
  setEmail,
}) => {
  setValue("role_id", data?.role_id);
  setRoleId(data?.role_id);
  handleChange(data?.role_id);
  setEmail(data?.email);
  setValue("email", data?.email);
};

const handleBoardMultiFields = (data, setValue, clearErrors) => {
  const fields = [
    "board_entity_type",
    "board_entity_type_2",
    "course_completed",
    "accredited_director",
  ];

  for (const field of fields) {
    if (!data?.[field]) continue;

    const values = data[field]
      .split(",")
      .map((id) =>
        field === "course_completed" || field === "accredited_director"
          ? Number.parseInt(id.trim())
          : id.trim()
      );

    setValue(field, values);
    clearErrors(field);
  }

  if (data?.past_board_roles) {
    const roles = data.past_board_roles.split(",").map((r) => r.trim());
    setValue("past_board_roles", roles);
    clearErrors("past_board_roles");
  }
};

const handleBoardRolesRepeater = (data, setValue, clearErrors) => {
  const boardRolesData = data?.board_roles_data;
  if (!boardRolesData) return;

  try {
    const roles =
      typeof boardRolesData === "string"
        ? JSON.parse(boardRolesData)
        : boardRolesData;

    const formattedRoles = roles.map((role) => ({
      rep_company_name: role.rep_company_name || "",
      rep_board_title: role.rep_board_title || "",
      rep_start_date: role.rep_start_date || "",
      rep_end_date: role.rep_end_date || "",
      is_currently_working: !!role.is_currently_working,
    }));

    setValue("board_roles", formattedRoles);
    clearErrors("board_roles");
  } catch (error) {
    console.error("Error parsing board_roles:", error);
    setValue("board_roles", []);
  }
};

const handleLanguage = (data, setValue) => {
  if (data?.language) {
    setValue("language", data.language.split(","));
  }
};

const handleAvailability = (
  data,
  setShowDateDiv,
  setValue,
  checkFromDate,
  checkToDate
) => {
  if (data?.user_availability == 2) {
    setShowDateDiv("block");

    setValue("availability", true);
    setValue(
      "from_date",
      checkFromDate(data?.user_availability_check?.start_time)
    );

    setValue(
      "to_date",
      checkToDate(data?.user_availability_check?.end_time)
    );

    return;
  }

  setShowDateDiv("none");
  setValue("availability", false);
  setValue("from_date", null);
  setValue("to_date", null);
};

export const applyVerificationDataWithDelay = ({
  data,
  reset,
  setValue,
  clearErrors,
  setSelectedFile,
  setImg,
  setResumeText,
  setSelectedResume,
  setShowDateDiv,
  checkFromDate,
  checkToDate,
  setDob,
  placeholderImage,
}) => {
  const { image, resume, ...rest } = data || {};

  reset(rest);

  setValue("resume", data?.resume);
  setSelectedFile(data?.image);

  setImg({
    src: data?.image || placeholderImage,
    alt: "Upload an Image",
  });

  setResumeText(data?.resume ? data.resume.split("resume/")[1] : "");
  setSelectedResume(data?.resume);

  handleBoardMultiFields(data, setValue, clearErrors);
  handleBoardRolesRepeater(data, setValue, clearErrors);
  handleLanguage(data, setValue);
  handleAvailability(data, setShowDateDiv, setValue, checkFromDate, checkToDate);

  setDob(data?.dob);
};

export const handlePostVerificationEffects = ({
  data,
  res,
  setting,
  setReadOnly,
  setEmailVerifyString,
  getObjectiveData,
  registerSetting,
  setUserId,
  setPopupMessage,
  setShowPopup,
  history,
  renderGlobalModal,
}) => {
  setReadOnly(true);
  setEmailVerifyString(data?.email_verify_string);
  getObjectiveData(data?.role_id);
  registerSetting(data?.role_id);
  setUserId(data?.id);
  setPopupMessage(res?.message);
  setShowPopup(true);

  if (
    setting?.admin_approval_required == 1 &&
    setting?.email_approval_required == 1
  ) {
    if (data?.email_verfication_status == 1) {
      if (data?.role_id != "" && data?.first_name != "") {
        setPopupMessage(res?.message);
        setShowPopup(true);
        history(`/`);
      }
    }
  }
};